import validateBalanceSheet from '@/validateBalanceSheet';
import { dateToSerialNumber } from '@/utils';
import lastOpenedSpreadsheet from '@/lastOpenedSpreadsheet';

const balanceSheet = {
  namespaced: true,

  state() {
    return {
      spreadsheet: null,
      lastOpened: lastOpenedSpreadsheet.get(),
      expenseItems: [],
      investmentItems: [],
      incomeItems: [],
      expenseCategories: [],
      expensePaymentMethods: [],
      expenseMerchantTypes: [],
      investmentTypes: [],
      investmentViaOptions: [],
      incomeSources: [],
    };
  },

  mutations: {
    setSpreadsheet(state, spreadsheet) {
      state.spreadsheet = spreadsheet;
    },

    setLastOpened(state, spreadsheet) {
      state.lastOpened = lastOpenedSpreadsheet.set(spreadsheet);
    },

    resetLastOpened(state) {
      lastOpenedSpreadsheet.reset();
      state.lastOpened = null;
    },

    setExpenseItems(state, items) {
      state.expenseItems = items;
    },

    setInvestmentItems(state, items) {
      state.investmentItems = items;
    },

    setIncomeItems(state, items) {
      state.incomeItems = items;
    },

    setExpenseCategories(state, expenseCategories) {
      state.expenseCategories = expenseCategories;
    },

    setExpensePaymentMethods(state, expensePaymentMethods) {
      state.expensePaymentMethods = expensePaymentMethods;
    },

    setExpenseMerchantTypes(state, expenseMerchantTypes) {
      state.expenseMerchantTypes = expenseMerchantTypes;
    },

    setInvestmentTypes(state, investmentTypes) {
      state.investmentTypes = investmentTypes;
    },

    setInvestmentViaOptions(state, investmentViaOptions) {
      state.investmentViaOptions = investmentViaOptions;
    },

    setIncomeSources(state, incomeSources) {
      state.incomeSources = incomeSources;
    },

    addExpenseItem(state, item) {
      state.expenseItems = [...state.expenseItems, item];
    },

    addInvestmentItem(state, item) {
      state.investmentItems = [...state.investmentItems, item];
    },

    addIncomeItem(state, item) {
      state.incomeItems = [...state.incomeItems, item];
    },
  },

  actions: {
    async loadExpenseItems({ commit, state }) {
      const [, ...items] = await state.spreadsheet.readRange('Expenses!A:I', {
        valueRenderOption: 'UNFORMATTED_VALUE',
      });

      commit('setExpenseItems', items);
    },

    async loadInvestmentItems({ commit, state }) {
      const [, ...items] = await state.spreadsheet.readRange(
        'Investments!A:F',
        {
          valueRenderOption: 'UNFORMATTED_VALUE',
        }
      );

      commit('setInvestmentItems', items);
    },

    async loadIncomeItems({ commit, state }) {
      const [, ...items] = await state.spreadsheet.readRange('Income!A:D', {
        valueRenderOption: 'UNFORMATTED_VALUE',
      });

      commit('setIncomeItems', items);
    },

    async loadExpenseCategories({ commit, state }) {
      const [, ...rows] = await state.spreadsheet.readRange(
        'Expense Categories'
      );

      const categories = rows.map(([name, ...subcategories]) => ({
        name,
        subcategories,
      }));

      commit('setExpenseCategories', categories);
    },

    async loadOptions({ commit, state }) {
      const [
        expensePaymentMethods,
        expenseMerchantTypes,
        investmentTypes,
        investmentViaOptions,
        incomeSources,
      ] = await state.spreadsheet.readRange('Options!A2:E', {
        majorDimension: 'COLUMNS',
      });

      commit('setExpensePaymentMethods', expensePaymentMethods);
      commit('setExpenseMerchantTypes', expenseMerchantTypes);
      commit('setInvestmentTypes', investmentTypes);
      commit('setInvestmentViaOptions', investmentViaOptions);
      commit('setIncomeSources', incomeSources);
    },

    async load({ commit, dispatch, state }, id) {
      try {
        const spreadsheet = await validateBalanceSheet(id);

        commit('setSpreadsheet', spreadsheet);

        await Promise.all([
          dispatch('loadExpenseItems'),
          dispatch('loadInvestmentItems'),
          dispatch('loadIncomeItems'),
          dispatch('loadExpenseCategories'),
          dispatch('loadOptions'),
        ]);

        commit('setLastOpened', spreadsheet);
      } catch (error) {
        if (state.lastOpened && id == state.lastOpened.id) {
          commit('resetLastOpened');
        }

        throw error;
      }
    },

    async addExpense({ commit, state }, data) {
      const res = await state.spreadsheet.append({
        sheet: 'Expenses',
        value: [
          dateToSerialNumber(new Date(data.date)),
          data.item,
          parseFloat(data.amount, 10),
          data.category,
          data.subcategory,
          data.paymentMethod,
          data.merchant,
          data.merchantType,
          data.description,
        ],
        valueInputOption: 'RAW',
        includeValuesInResponse: true,
        responseValueRenderOption: 'UNFORMATTED_VALUE',
      });

      commit('addExpenseItem', res.updatedData.values[0]);
    },

    async addInvestment({ commit, state }, data) {
      const res = await state.spreadsheet.append({
        sheet: 'Investments',
        value: [
          dateToSerialNumber(new Date(data.date)),
          data.product,
          data.type,
          parseFloat(data.amount, 10),
          data.via,
          data.description,
        ],
        valueInputOption: 'RAW',
        includeValuesInResponse: true,
        responseValueRenderOption: 'UNFORMATTED_VALUE',
      });

      commit('addInvestmentItem', res.updatedData.values[0]);
    },

    async addIncome({ commit, state }, data) {
      const res = await state.spreadsheet.append({
        sheet: 'Income',
        value: [
          dateToSerialNumber(new Date(data.date)),
          parseFloat(data.amount, 10),
          data.source,
          data.description,
        ],
        valueInputOption: 'RAW',
        includeValuesInResponse: true,
        responseValueRenderOption: 'UNFORMATTED_VALUE',
      });

      commit('addIncomeItem', res.updatedData.values[0]);
    },
  },
};

export default balanceSheet;
